<template>
  <div class="lookupTables">
    <div v-if="isTableNew && loadingCreation">
      <v-progress-circular
        :size="20"
        color="primary"
        class="mr-2"
        indeterminate
      ></v-progress-circular>
      Loading...
    </div>
    <div v-else-if="isTableNew">
      <v-form v-model="isNewTableValid" ref="newTableForm" lazy-validation>
        <v-row>
          <v-col cols="4">
            <v-text-field
              v-model="newTable.Name"
              label="Table name"
              class="mt-2"
              :style="{ 'max-width': '400px' }"
              :rules="rules.required"
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- NEW TABLE -->
        <div v-if="showFormButtons">
          <table class="newTable">
            <tr>
              <th>Column name</th>
              <th>Column type</th>
              <th>Length / Dp</th>
              <th></th>
            </tr>
            <tr v-for="(col, index) in newTable.Columns" :key="index" @click="editNewTable(col)">
              <td>{{ col.Name }}</td>
              <td>{{ col.FieldType }}</td>
              <td>
                {{ showDpLength(col) }}
              </td>
              <td>
                <v-icon @click.stop="deleteNewTableRow(col)">mdi-delete</v-icon>
              </td>
            </tr>
          </table>
        </div>
        <!-- END OF NEW TABLE -->
      </v-form>
    </div>

    <div v-else>
      <v-btn
        :disabled="!showFormButtons"
        color="primary"
        class="mb-4 mt-2"
        depressed
        @click="isTableNew = true"
      >
        New table
      </v-btn>
      <v-row>
        <v-col cols="4">
          <v-select
            v-model="selectedTable"
            :items="availableLookupTables"
            :loading="loadingLookupTables"
            label="Select table"
            :style="{ 'max-width': '400px' }"
            dense
            outlined
          ></v-select>
        </v-col>
      </v-row>

      <!-- DATA TABLE -->
      <div v-if="loadingTableData || loadingUpdate">
        <v-progress-circular
          :size="20"
          color="primary"
          class="mr-2"
          indeterminate
        ></v-progress-circular>
        Loading...
      </div>
      <div v-else-if="tableCols.Columns">
        <table class="dataTable">
          <thead>
            <tr>
              <th></th>
              <th v-for="col in tableCols.Columns" :key="col.Name" class="bd-b">{{ col.Name }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in tableData" :key="index" @click="editDataTable(row, index)">
              <td>
                <v-icon v-if="showFormButtons" @click.stop="deleteDataTableRow(row)"
                  >mdi-delete</v-icon
                >
              </td>
              <td v-for="(col, inx) in row" :key="inx" class="bd-b">
                {{ formatData(row, col, inx) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- END OF DATA TABLE -->
    </div>

    <!-- NEW TABLE DIALOG -->
    <v-dialog v-model="showNewTableDialog" width="400" persistent @keydown.esc="cancelNewTableRow">
      <v-card>
        <v-card-title>Edit column</v-card-title>
        <v-card-text>
          <v-form
            v-if="selectedNewTableRow"
            v-model="isNewTableRowValid"
            ref="newTableRowForm"
            lazy-validation
          >
            <div>
              <v-text-field
                v-model="selectedNewTableRow.Name"
                label="Column name"
                :rules="rules.required"
              ></v-text-field>
            </div>
            <div>
              <v-select
                v-model="selectedNewTableRow.FieldType"
                :items="columnTypesList"
                label="Column type"
                :rules="rules.required"
              ></v-select>
            </div>
            <div>
              <v-text-field
                v-if="
                  selectedNewTableRow.FieldType === 'String' ||
                  selectedNewTableRow.FieldType === 'Numeric'
                "
                v-model="selectedNewTableRow.Length"
                label="Length"
                :rules="rules.minLength"
                type="number"
                min="1"
              ></v-text-field>
            </div>
            <div>
              <v-text-field
                v-if="selectedNewTableRow.FieldType === 'Numeric'"
                v-model="selectedNewTableRow.Dp"
                type="number"
                label="Dp"
                min="1"
              ></v-text-field>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="cancelNewTableRow"> Cancel </v-btn>
          <v-btn color="primary" depressed @click="comfirmNewTableRow"> Ok </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- END OF NEW TABLE DIALOG -->

    <!-- DATA TABLE DIALOG -->
    <v-dialog
      v-model="showDataTableDialog"
      @keydown.esc="cancelDataTableRow"
      persistent
      :width="dialogWidth"
      :style="{ 'max-width': '800px' }"
    >
      <v-card>
        <v-card-title>Edit row</v-card-title>
        <v-card-text>
          <v-form
            v-if="selectedDataTableRow"
            v-model="isDataTableRowValid"
            ref="dataTableRowForm"
            lazy-validation
          >
            <div class="controlsContainer">
              <div v-for="col in tableCols.Columns" :key="col.Name" class="formControl">
                <v-text-field
                  v-if="col.FieldType === 'string' || col.FieldType === 'nvarchar'"
                  v-model="selectedDataTableRow[col.Name]"
                  :label="col.Name"
                  :max-length="col.Length"
                ></v-text-field>
                <v-text-field
                  v-if="col.FieldType === 'int' || col.FieldType === 'Integer'"
                  v-model="selectedDataTableRow[col.Name]"
                  :label="col.Name"
                  step="1"
                  type="number"
                ></v-text-field>
                <v-text-field
                  v-if="
                    col.FieldType === 'Numeric' ||
                    col.FieldType === 'numeric' ||
                    col.FieldType === 'float'
                  "
                  v-model="selectedDataTableRow[col.Name]"
                  :label="col.Name"
                  :step="calcStep(col.Dp)"
                  type="number"
                ></v-text-field>
                <div class="dateControl">
                  <DatePicker
                    v-if="col.FieldType === 'Date' || col.FieldType === 'date'"
                    v-model="selectedDataTableRow[col.Name]"
                    :label="col.Name"
                  />
                </div>
              </div>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="cancelDataTableRow"> Cancel </v-btn>
          <v-btn color="primary" depressed @click="comfirmDataTableRow"> Ok </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- END OF DATA TABLE DIALOG -->

    <!-- DELETE CONFIRMATION DIALOG -->
    <Dialog ref="confirmationDialog" v-model="dialog" />

    <v-footer
      v-if="showFormButtons"
      padless
      fixed
      outlined
      class="pl-5 pr-5 pt-3 pb-3"
      color="white"
      elevation="3"
    >
      <!-- NEW TABLE BUTTONS -->
      <div v-if="isTableNew" class="btnsContainer">
        <v-btn class="mr-4" color="primary" outlined @click="addCol">Add column</v-btn>
        <div class="pl-3">
          <v-scroll-y-transition>
            <div v-if="!areNewRowsValid" class="red--text">
              All table values should be set before creation
            </div>
          </v-scroll-y-transition>
        </div>
        <v-spacer></v-spacer>
        <v-btn class="mr-4" color="primary" outlined @click="cancelNewTableCreation">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :disabled="!areNewRowsValid"
          :loading="loadingCreation"
          @click="onCreate"
        >
          Create
        </v-btn>
      </div>

      <!-- DATA TABLE BUTTONS -->
      <div v-else class="btnsContainer">
        <v-btn class="mr-4" color="primary" :disabled="disabledAddRow" outlined @click="addRow">
          Add row
        </v-btn>
        <div class="pl-3">
          <v-scroll-y-transition>
            <div v-if="!areDataRowsValid" class="red--text">
              At least one cell in a row should have value
            </div>
          </v-scroll-y-transition>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="!tableCols.Name"
          class="mr-4"
          color="primary"
          outlined
          @click="cancelDataTable"
          >Cancel</v-btn
        >
        <v-btn
          color="primary"
          depressed
          :disabled="!tableCols.Name || !areDataRowsValid"
          :loading="loadingUpdate"
          @click="onSave"
          >Save</v-btn
        >
      </div>
    </v-footer>
  </div>
</template>

<script>
import Dialog from '@/components/layout/Dialog.vue';
import DatePicker from '@/components/formControls/DatePicker.vue';
import Utils from '@/utils';

export default {
  components: { Dialog, DatePicker },
  name: 'Lookups',

  data() {
    return {
      // COMMON
      isTableNew: false,
      dialog: false,
      rules: {
        required: [(v) => !!v || 'Required.'],
        minLength: [(v) => v > 0 || 'Length should be at least 1'],
      },

      // DATA TABLE
      loadingLookupTables: false,
      loadingTableData: false,
      loadingUpdate: false,

      selectedTable: null,
      availableLookupTables: [],
      tableCols: {},
      tableData: [],

      selectedDataTableRow: {},
      selectedDataTableRowIndex: null,
      showDataTableDialog: false,
      isDataTableRowValid: false,
      baseDialogWidth: 400,

      // NEW TABLE
      loadingCreation: false,
      newTable: {
        Name: null,
        Columns: [
          {
            Name: '',
            FieldType: '',
            Dp: 0,
            Key: true,
            Length: 0,
          },
        ],
      },
      defaultNewTable: {
        Name: null,
        Columns: [
          {
            Name: '',
            FieldType: '',
            Dp: 0,
            Key: true,
            Length: 0,
          },
        ],
      },
      columnTypesList: ['String', 'Integer', 'Numeric', 'Date'],

      isNewTableRowValid: false,
      isNewTableValid: false,

      showNewTableDialog: false,
      selectedNewTableRow: {},
      defaultNewTableRow: {
        Name: '',
        FieldType: '',
        Dp: 0,
        Key: false,
        Length: 0,
      },
      selectedNewTableRowIndex: null,
    };
  },

  computed: {
    areNewRowsValid() {
      return this.newTable.Columns.every((col) => !!col.Name && !!col.FieldType);
    },

    areDataRowsValid() {
      return this.tableData.every((row) => {
        let result = false;
        for (let prop in row) {
          if (row[prop]) result = true; 
        }
        return result;
      });
    },

    disabledAddRow() {
      if (this.tableCols.Columns) return !this.tableCols.Columns.length;
      return true;
    },

    defaultDataTableRow() {
      if (this.tableCols.Columns) {
        const defaultRow = {};
        this.tableCols.Columns.forEach((col) => (defaultRow[col.Name] = null));
        return defaultRow;
      }
      return null;
    },

    dialogWidth() {
      if (this.tableCols.Columns) {
        let count = this.tableCols.Columns.length;
        if (count > 1) count -= 1;
        let width = this.baseDialogWidth + 70 * count;
        return width > 800 ? 800 : width;
      }
      return this.baseDialogWidth;
    },

    numCellsToFormat() {
      const result = [];
      if (this.tableCols.Columns) {
        this.tableCols.Columns.forEach((col) => {
          if (
            col.FieldType === 'Numeric' ||
            col.FieldType === 'numeric' ||
            col.FieldType === 'float'
          ) {
            result.push({ name: col.Name, dp: col.Dp });
          }
        });
      }
      return result;
    },

    dateCellsToFormat() {
      const result = [];
      if (this.tableCols.Columns) {
        this.tableCols.Columns.forEach((col) => {
          if (col.FieldType === 'Date' || col.FieldType === 'date') {
            result.push(col.Name);
          }
        });
      }
      return result;
    },

    selectedNewTableRowType() {
      return this.selectedNewTableRow.FieldType || null;
    },

    showFormButtons() {
      const userPermission = this.$store.getters.getUserPermission(this.$route.path);
      if (userPermission && userPermission == 'Full') return true;

      return false;
    },
  },

  watch: {
    selectedTable() {
      if (this.selectedTable) this.getTableDefinition();
    },
    selectedNewTableRowType() {
      if (!this.selectedNewTableRow.Length && this.selectedNewTableRowType === 'Numeric') {
        this.selectedNewTableRow.Length = 15;
        this.selectedNewTableRow.Dp = 3;
      } else if (!this.selectedNewTableRow.Length) {
        this.selectedNewTableRow.Length = 0;
        this.selectedNewTableRow.Dp = 0;
      }
    },
  },

  created() {
    this.getLookupTables();
  },

  methods: {
    // DATA TABLE
    formatData(row, col, inx) {
      const cellFormat = this.numCellsToFormat.find((x) => x.name === inx);

      if (cellFormat) {
        const numberFormat = new Intl.NumberFormat('en-GB', {
          maximumFractionDigits: cellFormat.dp,
          minimumFractionDigits: cellFormat.dp,
        });
        col = numberFormat.format(col);
      } else if (this.dateCellsToFormat.includes(inx)) {
        col = Utils.getDateStr(new Date(col));
      }
      return col;
    },

    calcStep(dp) {
      let step = 1;
      let i = 0;
      while (i < dp) {
        step /= 10;
        i++;
      }
      return step;
    },

    cancelDataTable() {
      this.tableCols = {};
      this.tableData = [];
      this.selectedTable = null;
    },

    addRow() {
      const newRow = {};
      this.tableCols.Columns.forEach((col) => {
        newRow[col.Name] = null;
      });
      this.tableData.push(newRow);
    },

    editDataTable(row) {
      if (!this.showFormButtons) return;
      this.showDataTableDialog = true;
      this.selectedDataTableRow = { ...row };
      this.selectedDataTableRowIndex = this.tableData.indexOf(row);
    },

    comfirmDataTableRow() {
      this.isDataTableRowValid = this.$refs.dataTableRowForm.validate();
      if (this.isDataTableRowValid) {
        this.tableData.splice(this.selectedDataTableRowIndex, 1, this.selectedDataTableRow);
        this.resetDataTableRowValues();
      }
    },

    cancelDataTableRow() {
      this.resetDataTableRowValues();
    },

    resetDataTableRowValues() {
      this.showDataTableDialog = false;
      this.selectedDataTableRow = { ...this.defaultDataTableRow };
      this.selectedDataTableRowIndex = null;
    },

    onSave() {
      if (this.areDataRowsValid) this.updateLookupData();
    },

    async deleteDataTableRow(row) {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete this record?'
        );
        const index = this.tableData.indexOf(row);
        this.tableData.splice(index, 1);
      } catch (err) {
        console.log(err);
      }
    },
    // END OF DATA TABLE

    // NEW TABLE
    showDpLength(col) {
      if (col.FieldType === 'Numeric') return `${col.Length}/${col.Dp}`;
      if (col.FieldType === 'String') return `${col.Length}`;
      return '-';
    },

    cancelNewTableCreation() {
      this.isTableNew = false;
      this.newTable = { ...this.defaultNewTable };
    },

    addCol() {
      this.newTable.Columns.push({
        Name: '',
        FieldType: '',
        Dp: 0,
        Key: false,
        Length: 0,
      });
    },

    editNewTable(col) {
      this.showNewTableDialog = true;
      this.selectedNewTableRow = { ...col };
      this.selectedNewTableRowIndex = this.newTable.Columns.indexOf(col);
    },

    comfirmNewTableRow() {
      this.isNewTableRowValid = this.$refs.newTableRowForm.validate();
      if (this.isNewTableRowValid) {
        this.newTable.Columns.splice(this.selectedNewTableRowIndex, 1, this.selectedNewTableRow);
        this.resetNewTableRowValues();
      }
    },

    cancelNewTableRow() {
      this.resetNewTableRowValues();
    },

    resetNewTableRowValues() {
      this.showNewTableDialog = false;
      this.selectedNewTableRow = { ...this.defaultNewTableRow };
      this.selectedNewTableRowIndex = null;
    },

    onCreate() {
      this.isNewTableValid = this.$refs.newTableForm.validate();
      if (this.isNewTableValid && this.areNewRowsValid) this.createNewTable();
    },

    async deleteNewTableRow(col) {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete this record?'
        );
        const index = this.newTable.Columns.indexOf(col);
        this.newTable.Columns.splice(index, 1);
      } catch (err) {
        console.log(err);
      }
    },
    // END OF NEW TABLE

    async getLookupTables() {
      this.loadingLookupTables = true;
      try {
        const response = await this.$api.get('/Lookup/GetLookupTables');

        this.availableLookupTables = response.data;
      } catch (err) {
        console.log(err.message);
      } finally {
        this.loadingLookupTables = false;
      }
    },

    async getTableDefinition() {
      this.loading = true;
      try {
        const response = await this.$api.get(
          '/Lookup/GetTableDefinition?tableName=' + this.selectedTable
        );

        this.tableCols = response.data;
        this.getTableData();
      } catch (err) {
        console.log(err.message);
      } finally {
        this.loading = false;
      }
    },

    async getTableData() {
      this.loadingTableData = true;
      try {
        const response = await this.$api.get(
          '/Lookup/GetLookupTableData?tableName=' + this.selectedTable
        );

        this.tableData = response.data;
      } catch (err) {
        console.log(err.message);
      } finally {
        this.loadingTableData = false;
      }
    },

    async createNewTable() {
      this.loadingCreation = true;
      try {
        await this.$api.post('/Lookup/CreateTable', this.newTable);

        this.getLookupTables();
        this.isTableNew = false;
      } catch (err) {
        console.log(err.message);
      } finally {
        this.loadingCreation = false;
        this.newTable = this.defaultNewTable;
      }
    },

    async updateLookupData() {
      this.loadingUpdate = true;
      const data = {
        TableName: this.tableCols.Name.replace('utbl_', ''),
        Data: JSON.stringify(this.tableData),
      };
      try {
        await this.$api.post('/Lookup/UpdateLookupData', data);

        this.getTableData();
      } catch (err) {
        console.log(err.message);
      } finally {
        this.loadingUpdate = false;
        this.isTableNew = false;
        this.newTable = this.defaultNewTable;
      }
    },
  },
};
</script>

<style scoped>
.lookupTables {
  margin-bottom: 54px;
  padding-left: 30px;
  padding-right: 30px;
}
.btnsContainer {
  /* padding: 10px; */
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
table {
  border-collapse: collapse;
}
.dataTable {
  min-width: 400px;
}
.newTable {
  min-width: 800px;
}
tr {
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}
td,
th {
  padding: 0 8px;
}
th {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  height: 32px;
  text-align: left;
}
td {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.87);
  text-align: left;
  height: 32px;
}
tr:not(:first-child):hover {
  cursor: pointer;
  background-color: #f2f2f2;
}
.newTableError {
  height: 18px;
}
.controlsContainer {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  column-gap: 20px;
}
.dateControl {
  margin-top: 6px;
}
.newDialog {
  transition: height 150ms linear;
}
</style>
